import { ChevronDown, Loader2, LucideIcon, MoreHorizontal } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";
import Tooltip from "./Tooltip";

export interface ActionItem {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  variant?: "default" | "danger" | "warning" | "success";
  disabled?: boolean;
  hidden?: boolean;
  /** Mostra um spinner no lugar do ícone e desabilita o item (ex.: ação em andamento nesta linha). */
  loading?: boolean;
  /** Renderiza um divisor acima deste item — use para separar ações destrutivas/menos comuns do restante do menu. */
  divider?: boolean;
}

interface ActionDropdownProps {
  actions: ActionItem[];
  label?: string;
  icon?: LucideIcon;
  buttonClassName?: string;
  menuClassName?: string;
  align?: "left" | "right";
  size?: "sm" | "md" | "lg";
  /** "compact" reproduz o visual do menu "Mais opções" do card de Postagem
   * (itens em caixa normal, densos, sem uppercase/translate) em vez do
   * padrão uppercase/bold usado por Anúncios e Campanhas. */
  variant?: "default" | "compact";
  /** Tooltip no botão-gatilho (útil quando não há `label` visível, ex.: ícone sozinho). */
  tooltip?: string;
  /** Mostra spinner no botão-gatilho e bloqueia abertura (ex.: alguma ação desta linha está em andamento). */
  loading?: boolean;
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({
  actions,
  label,
  icon: Icon = MoreHorizontal,
  buttonClassName,
  menuClassName,
  align = "right",
  size = "md",
  variant = "default",
  tooltip,
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    width: 0,
    dropUp: false,
  });
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const visibleActions = actions.filter((action) => !action.hidden);

  const updateCoords = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Estimativa da altura do menu (aprox. 44px por item + padding)
      const menuHeight = visibleActions.length * 44 + 20;
      const spaceBelow = window.innerHeight - rect.bottom;

      // Abre para cima se não houver espaço embaixo e houver mais espaço em cima
      const dropUp = spaceBelow < menuHeight && rect.top > spaceBelow;

      setCoords({
        top: rect.top + window.scrollY,
        bottom: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        dropUp,
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      updateCoords();
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", updateCoords, true);
      window.addEventListener("resize", updateCoords);

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") setIsOpen(false);
      };
      document.addEventListener("keydown", handleEscape);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", updateCoords, true);
        window.removeEventListener("resize", updateCoords);
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isOpen]);

  if (visibleActions.length === 0) return null;

  const triggerButton = (
    <button
      ref={buttonRef}
      disabled={loading}
      onClick={(e) => {
        e.stopPropagation();
        if (loading) return;
        setIsOpen(!isOpen);
      }}
      className={cn(
        "flex items-center rounded-xl transition-all duration-200 font-black uppercase tracking-widest text-[10px]",
        label ? "gap-2" : "justify-center",
        "border border-purple-200 bg-white hover:bg-purple-50 hover:border-purple-300 text-gray-600",
        "shadow-xs active:scale-95",
        size === "sm" && (label ? "h-8 px-3" : "h-8 w-8"),
        size === "md" && (label ? "h-10 px-4" : "h-10 w-10"),
        size === "lg" && (label ? "h-12 px-6" : "h-12 w-12"),
        isOpen && "bg-purple-50 border-purple-400 ring-2 ring-purple-100",
        loading && "opacity-60 cursor-not-allowed",
        buttonClassName,
      )}
    >
      {label && <span>{label}</span>}
      {loading ? (
        <Loader2 className={cn("h-4 w-4 animate-spin", label && "ml-0.5")} />
      ) : (
        <Icon className={cn("h-4 w-4", label && "ml-0.5")} />
      )}
      {label && !loading && (
        <ChevronDown
          className={cn(
            "h-3 w-3 text-gray-400 transition-transform",
            isOpen && "rotate-180",
          )}
        />
      )}
    </button>
  );

  return (
    <div className="relative inline-block text-left">
      {tooltip ? (
        <Tooltip content={tooltip} position="left">
          {triggerButton}
        </Tooltip>
      ) : (
        triggerButton
      )}

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "absolute",
              top: coords.dropUp ? "auto" : `${coords.bottom + 8}px`,
              bottom: coords.dropUp
                ? `${document.documentElement.scrollHeight - coords.top + 8}px`
                : "auto",
              left:
                align === "right"
                  ? `${coords.left + coords.width}px`
                  : `${coords.left}px`,
              transform: align === "right" ? "translateX(-100%)" : "none",
            }}
            className={cn(
              variant === "compact"
                ? "w-64 bg-white rounded-lg border border-purple-200 shadow-xl p-1.5 space-y-0.5 z-9999"
                : "min-w-[220px] bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-purple-200 py-2 z-9999",
              "animate-in fade-in zoom-in-95 duration-200",
              coords.dropUp ? "slide-in-from-bottom-2" : "slide-in-from-top-2",
              menuClassName,
            )}
          >
            {visibleActions.map((action, index) => {
              const ActionIcon = action.icon;
              return (
                <React.Fragment key={index}>
                  {action.divider && (
                    <div
                      className={cn(
                        variant === "compact"
                          ? "my-1 border-t border-gray-100"
                          : "my-1.5 mx-2 border-t border-purple-100",
                      )}
                    />
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (action.disabled || action.loading) return;
                      action.onClick();
                      setIsOpen(false);
                    }}
                    disabled={action.disabled || action.loading}
                    className={cn(
                      variant === "compact"
                        ? "w-full flex items-center gap-2 px-2.5 py-1.5 text-[13px] font-medium rounded-md transition-colors text-left group"
                        : "w-full flex items-center gap-3 px-4 py-2.5 text-[11px] font-black uppercase tracking-widest transition-all text-left hover:translate-x-1",
                      action.disabled || action.loading
                        ? "opacity-40 cursor-not-allowed"
                        : "cursor-pointer",
                      action.variant === "danger"
                        ? variant === "compact"
                          ? "text-red-600 hover:bg-red-50"
                          : "text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        : action.variant === "warning"
                          ? variant === "compact"
                            ? "text-amber-600 hover:bg-amber-50"
                            : "text-orange-600 hover:bg-orange-50"
                          : action.variant === "success"
                            ? variant === "compact"
                              ? "text-emerald-600 hover:bg-emerald-50"
                              : "text-green-600 hover:bg-green-50"
                            : variant === "compact"
                              ? "text-gray-700 hover:bg-gray-100/80 hover:text-purple-600"
                              : "text-gray-700 hover:bg-purple-50 hover:text-purple-700",
                    )}
                  >
                    {action.loading ? (
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                    ) : (
                      ActionIcon && (
                        <ActionIcon
                          className={cn(
                            "h-4 w-4 shrink-0",
                            variant === "compact" &&
                              action.variant === undefined &&
                              "text-gray-400 group-hover:text-purple-500",
                          )}
                        />
                      )
                    )}
                    <span className={variant === "compact" ? "flex-1" : ""}>
                      {action.label}
                    </span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ActionDropdown;
